import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Spin, Empty } from "antd";
import { blogAPI } from "../services/api";
import { titleToSlug } from "../utils/blogUtils";
import Header from "./header";
import Footer from "./footer";
import ContactForm from "./ContactForm";
import "../styles/blog-single.css";

export default function BlogSingle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getBySlug(slug);
      setBlog(response.data.data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
      setBlog(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (blog && blog.title) {
      const imageUrl = blog.featured_image
        ? (blog.featured_image.startsWith('/uploads')
          ? `${process.env.REACT_APP_ADMIN_API_URL || 'https://ampsweb.saassrs.com'}${blog.featured_image}`
          : blog.featured_image)
        : `${window.location.origin}/images/backimg1.jpg`;

      const description = blog.excerpt || blog.content?.replace(/<[^>]*>/g, '').substring(0, 160) || '';

      const blogUrl = `${window.location.origin}/blogs/${blog.slug || titleToSlug(blog.title)}`;

      const setMetaTag = (name, content, isProperty = false) => {
        const attr = isProperty ? 'property' : 'name';
        let tag = document.querySelector(`meta[${attr}="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attr, name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      setMetaTag('description', description);
      setMetaTag('og:title', blog.title, true);
      setMetaTag('og:description', description, true);
      setMetaTag('og:url', blogUrl, true);
      setMetaTag('og:type', 'article', true);
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', blog.title);
      setMetaTag('twitter:description', description);
      setMetaTag('twitter:image', imageUrl);

      if (blog.author_name) {
        setMetaTag('article:author', blog.author_name, true);
      }
    }
  }, [blog]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return "";
    }
  };

  const getImageUrl = (blog) => {
    if (blog?.featured_image) {
      if (blog.featured_image.startsWith('/uploads')) {
        const backendUrl = process.env.REACT_APP_ADMIN_API_URL || 'https://ampsweb.saassrs.com';
        return `${backendUrl}${blog.featured_image}`;
      }
      return blog.featured_image;
    }
    return "/images/backimg1.jpg";
  };

  if (loading) {
    return (
      <main>
        <Header />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <Spin size="large" />
        </div>
        <Footer />
      </main>
    );
  }

  if (!blog) {
    return (
      <main>
        <Header />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <Empty description="Blog not found" />
          <Link to="/blogs" style={{ marginTop: '20px', display: 'inline-block' }}>
            ← Back to Blogs
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Header />
      <article className="blog-single">
        <div className="blog-single__container">
          <Link to="/blogs" className="blog-single__back">
            ← Back to Blogs
          </Link>

          {blog.featured_image && (
            <div className="blog-single__image">
              <img
                src={getImageUrl(blog)}
                alt={blog.title}
                onError={(e) => {
                  e.target.src = "/images/backimg1.jpg";
                }}
              />
            </div>
          )}

          <div className="blog-single__content">
            <h1 className="blog-single__title">{blog.title}</h1>

            <div className="blog-single__meta">
              {blog.author_name && (
                <span className="blog-single__author">By {blog.author_name}</span>
              )}
              {blog.created_at && (
                <span className="blog-single__date">{formatDate(blog.created_at)}</span>
              )}
              {blog.views > 0 && (
                <span className="blog-single__views">{blog.views} views</span>
              )}
            </div>

            {blog.excerpt && (
              <p className="blog-single__excerpt">{blog.excerpt}</p>
            )}

            <div
              className="blog-single__body"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </div>
      </article>

      <ContactForm />

      <Footer />
    </main>
  );
}

